<template>
	<div ref="typeformWrapper" class="typeform-wrapper">
		<div ref="typeformContainer" class="typeform-container" :data-tf-widget="code" data-tf-inline-on-mobile data-tf-medium="snippet" />
		<Button class="close-btn" :label="false" icon="ic-close" icon-position="front" tone="secondary" :click-func="animateOut" />
	</div>
</template>

<script setup>
import { gsap } from 'gsap'
import { Button } from '~/components/primitives'
import { useEmitter } from '~/composables/core'

const TYPEFORM_SCRIPT_SRC = 'https://embed.typeform.com/next/embed.js'

const props = defineProps({
	code: {
		type: String,
		default: 'PbA4IX71',
	},
	autoOpen: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['close'])

const typeformWrapper = ref(null)
const typeformContainer = ref(null)
const isOpen = ref(false)
const emitter = useEmitter()

let offTypeformClose = null
let offTypeformOpen = null

const ensureTypeformScript = () => {
	if (!import.meta.client) return Promise.resolve()

	return new Promise((resolve, reject) => {
		const existingScript = document.querySelector(`script[src="${TYPEFORM_SCRIPT_SRC}"]`)
		if (existingScript) {
			if (window.tf) {
				resolve()
				return
			}

			if (existingScript.dataset.tfLoadFailed === 'true') {
				reject(new Error('Typeform embed script failed to load.'))
				return
			}

			if (existingScript.dataset.tfLoaded === 'true') {
				reject(new Error('Typeform embed script loaded but window.tf is unavailable.'))
				return
			}

			existingScript.addEventListener('load', () => resolve(), { once: true })
			existingScript.addEventListener('error', (error) => reject(error), { once: true })
			return
		}

		const script = document.createElement('script')
		script.src = TYPEFORM_SCRIPT_SRC
		script.async = true
		script.onload = () => {
			script.dataset.tfLoaded = 'true'
			resolve()
		}
		script.onerror = (error) => {
			script.dataset.tfLoadFailed = 'true'
			reject(error)
		}
		document.head.appendChild(script)
	})
}

const initTypeform = async () => {
	if (!import.meta.client || !typeformContainer.value) return

	try {
		await ensureTypeformScript()
		window.tf?.load?.()
	} catch (error) {
		if (import.meta.dev) {
			console.warn('Failed to load Typeform embed script.', error)
		}
	}
}

const animateIn = async () => {
	if (!typeformWrapper.value || isOpen.value) return
	await initTypeform()

	isOpen.value = true
	document.body.style.cursor = 'progress'
	gsap.killTweensOf(typeformWrapper.value)
	gsap.to(typeformWrapper.value, {
		duration: 0.8,
		y: 0,
		yPercent: 0,
		ease: 'power3.inOut',
		onComplete: () => {
			document.body.style.cursor = ''
		},
	})
}

const animateOut = () => {
	if (!typeformWrapper.value || !isOpen.value) return
	isOpen.value = false

	gsap.to(typeformWrapper.value, {
		duration: 0.8,
		y: 0,
		yPercent: -100,
		ease: 'power3.inOut',
		onComplete: () => {
			emit('close')
		},
	})
}

const onKeyDown = (event) => {
	if (event.key === 'Escape') {
		animateOut()
	}
}

defineExpose({
	animateIn,
	animateOut,
})

onBeforeUnmount(() => {
	offTypeformClose?.()
	offTypeformClose = null
	offTypeformOpen?.()
	offTypeformOpen = null
	document.removeEventListener('keydown', onKeyDown)
	gsap.killTweensOf(typeformWrapper.value)
	document.body.style.cursor = ''
})

onMounted(() => {
	if (props.autoOpen) {
		animateIn()
	}
	offTypeformClose = emitter.on('typeform-close', animateOut)
	offTypeformOpen = emitter.on('typeform-open', animateIn)
	document.addEventListener('keydown', onKeyDown)
})
</script>

<style lang="scss" scoped>
.typeform-wrapper {
	position: fixed;
	top: 0;
	left: 0;
	z-index: 10;
	width: 100vw;
	height: 100vh;
	min-height: 100svh;
	height: 100dvh;
	transform: translateY(-100%);

	z-index: 11;

	color: var(--color-geral-white);
	background-color: var(--color-geral-white);

	.typeform-container {
		position: relative;

		display: block;
		width: 100%;
		height: 100%;
	}

	.close-btn {
		position: absolute;
		top: rs(20, 12);
		right: rs(20, 12);
		z-index: 1000;
	}

	.close-btn :deep(.inner) {
		svg {
			width: 24px;
		}
	}
}
</style>
